import type { QuotedMessage } from '@nexo/types';
import { cn, useToast } from '@nexo/ui';
import { Paperclip, Reply, X } from 'lucide-react';
import { RichText } from './RichText';
import { jumpToMessage } from './replying';
import { useTranslation } from 'react-i18next';

/** Quoted message above an inline reply (or above the composer while replying). */
export function QuoteBlock({
  quote,
  onClose,
  className,
}: {
  quote: QuotedMessage;
  onClose?: () => void;
  className?: string;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const text = quote.body.split('\n')[0] ?? '';
  return (
    <div
      className={cn(
        'flex max-w-xl items-stretch gap-2 overflow-hidden rounded-lg border-l-[3px] border-accent bg-surface-2/80 text-[13px]',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => {
          if (!jumpToMessage(quote.id)) {
            toast({
              title: t('El mensaje original es más antiguo'),
              description: t('Sube en la conversación para verlo.'),
            });
          }
        }}
        className="min-w-0 flex-1 px-2.5 py-1.5 text-left hover:bg-surface-2"
        title={t('Ir al mensaje')}
      >
        <span className="flex items-center gap-1 text-xs font-semibold text-accent">
          <Reply className="size-3" /> {quote.author.displayName}
        </span>
        <span className="line-clamp-2 break-words text-muted">
          {text ? <RichText text={text} /> : null}
          {quote.attachmentCount > 0 && (
            <span className="inline-flex items-center gap-0.5">
              {text && ' · '}
              <Paperclip className="size-3" />
              {quote.attachmentCount === 1
                ? t('Archivo')
                : t('{{count}} archivos', { count: quote.attachmentCount })}
            </span>
          )}
        </span>
      </button>
      {onClose && (
        <button
          type="button"
          aria-label={t('Cancelar respuesta')}
          title={t('Cancelar respuesta (Esc)')}
          onClick={onClose}
          className="px-2 text-subtle hover:text-text"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
}
